import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';
import {
  ShareNetwork,
  Download,
  SpinnerGap,
  MapPin,
  ForkKnife,
  Camera,
} from '@phosphor-icons/react';
import { Button } from './ui/button';

const CARD_W = 1080;
const CARD_H = 1080;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => reject(new Error('timeout')), 5000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); reject(new Error(`Failed to load: ${src}`)); };
    img.src = src;
  });
}

function drawImageCover(ctx, img, x, y, w, h, radius) {
  ctx.save();
  if (radius) {
    roundRect(ctx, x, y, w, h, radius);
    ctx.clip();
  }
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

const ShareCheckinCard = ({ restaurantName, address, mealType, photos, date }) => {
  const [generating, setGenerating] = useState(false);

  const generateCard = useCallback(async () => {
    setGenerating(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = CARD_W;
      canvas.height = CARD_H;
      const ctx = canvas.getContext('2d');

      // ── Background ──
      const grad = ctx.createLinearGradient(0, 0, 0, CARD_H);
      grad.addColorStop(0, '#1A1918');
      grad.addColorStop(0.5, '#2C2826');
      grad.addColorStop(1, '#1A1918');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CARD_W, CARD_H);

      // Subtle texture dots
      ctx.fillStyle = 'rgba(255,255,255,0.015)';
      for (let i = 0; i < 300; i++) {
        const px = Math.random() * CARD_W;
        const py = Math.random() * CARD_H;
        ctx.beginPath();
        ctx.arc(px, py, Math.random() * 2 + 0.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Decorative circles
      ctx.fillStyle = 'rgba(169, 68, 56, 0.12)';
      ctx.beginPath();
      ctx.arc(CARD_W * 0.88, CARD_H * 0.08, 180, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'rgba(196, 163, 90, 0.08)';
      ctx.beginPath();
      ctx.arc(CARD_W * 0.12, CARD_H * 0.92, 200, 0, Math.PI * 2);
      ctx.fill();

      // ── Top branding bar ──
      ctx.fillStyle = '#A94438';
      roundRect(ctx, 60, 50, 200, 44, 22);
      ctx.fill();

      // Fork icon as text
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '600 20px system-ui';
      ctx.textAlign = 'left';
      ctx.fillText('\u{1F374}', 78, 79);

      ctx.fillStyle = '#FFFFFF';
      ctx.font = '700 16px system-ui';
      ctx.fillText('DiningTracker.co.za', 105, 79);

      // ── "I'm dining at" label ──
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '500 28px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText("I'm dining at", CARD_W / 2, 160);

      // ── Restaurant name ──
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '800 56px system-ui';
      ctx.textAlign = 'center';

      // Word wrap for long names
      const maxNameWidth = CARD_W - 120;
      const nameLines = wrapText(ctx, restaurantName || 'Unknown', maxNameWidth);
      let nameY = 230;
      nameLines.forEach((line) => {
        ctx.fillText(line, CARD_W / 2, nameY);
        nameY += 68;
      });

      // ── Location ──
      if (address) {
        const shortAddr = address.length > 50 ? address.substring(0, 50) + '...' : address;
        ctx.fillStyle = '#C4A35A';
        ctx.font = '500 24px system-ui';
        ctx.fillText(shortAddr, CARD_W / 2, nameY + 10);
        nameY += 40;
      }

      // ── Meal type pill ──
      if (mealType) {
        const pillText = mealType.charAt(0).toUpperCase() + mealType.slice(1);
        ctx.font = '600 20px system-ui';
        const tw = ctx.measureText(pillText).width;
        const pillW = tw + 40;
        const pillX = (CARD_W - pillW) / 2;
        const pillY = nameY + 10;

        ctx.fillStyle = 'rgba(196, 163, 90, 0.2)';
        roundRect(ctx, pillX, pillY, pillW, 36, 18);
        ctx.fill();

        ctx.fillStyle = '#C4A35A';
        ctx.textAlign = 'center';
        ctx.fillText(pillText, CARD_W / 2, pillY + 25);
        nameY = pillY + 56;
      }

      // ── Photos grid ──
      const photoStartY = Math.max(nameY + 20, 420);
      const photoImages = [];

      for (const p of (photos || []).slice(0, 4)) {
        try {
          const src = p.preview || p.url;
          if (src) {
            const img = await loadImage(src);
            photoImages.push(img);
          }
        } catch {
          // skip failed images
        }
      }

      if (photoImages.length > 0) {
        const margin = 60;
        const gap = 16;
        const totalW = CARD_W - margin * 2;
        const totalH = CARD_H - photoStartY - 120;

        if (photoImages.length === 1) {
          drawImageCover(ctx, photoImages[0], margin, photoStartY, totalW, totalH, 24);
        } else if (photoImages.length === 2) {
          const w = (totalW - gap) / 2;
          drawImageCover(ctx, photoImages[0], margin, photoStartY, w, totalH, 24);
          drawImageCover(ctx, photoImages[1], margin + w + gap, photoStartY, w, totalH, 24);
        } else if (photoImages.length === 3) {
          const bigW = (totalW - gap) * 0.55;
          const smallW = totalW - bigW - gap;
          const smallH = (totalH - gap) / 2;
          drawImageCover(ctx, photoImages[0], margin, photoStartY, bigW, totalH, 24);
          drawImageCover(ctx, photoImages[1], margin + bigW + gap, photoStartY, smallW, smallH, 24);
          drawImageCover(ctx, photoImages[2], margin + bigW + gap, photoStartY + smallH + gap, smallW, smallH, 24);
        } else {
          const w = (totalW - gap) / 2;
          const h = (totalH - gap) / 2;
          drawImageCover(ctx, photoImages[0], margin, photoStartY, w, h, 24);
          drawImageCover(ctx, photoImages[1], margin + w + gap, photoStartY, w, h, 24);
          drawImageCover(ctx, photoImages[2], margin, photoStartY + h + gap, w, h, 24);
          drawImageCover(ctx, photoImages[3], margin + w + gap, photoStartY + h + gap, w, h, 24);
        }
      } else {
        // No photos — show decorative placeholder
        ctx.fillStyle = 'rgba(169, 68, 56, 0.1)';
        roundRect(ctx, 60, photoStartY, CARD_W - 120, CARD_H - photoStartY - 120, 24);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.font = '400 32px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('No photos yet', CARD_W / 2, photoStartY + (CARD_H - photoStartY - 120) / 2 + 10);
      }

      // ── Date stamp ──
      if (date) {
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.font = '500 20px system-ui';
        ctx.textAlign = 'right';
        ctx.fillText(date, CARD_W - 60, CARD_H - 50);
      }

      // ── Bottom branding ──
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.font = '500 20px system-ui';
      ctx.textAlign = 'left';
      ctx.fillText('DiningTracker.co.za', 60, CARD_H - 50);

      return canvas;
    } catch (err) {
      console.error('Card generation error:', err);
      throw err;
    }
  }, [restaurantName, address, mealType, photos, date]);

  const handleShare = async () => {
    setGenerating(true);
    try {
      const canvas = await generateCard();
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
      const file = new File([blob], `checkin-${restaurantName?.replace(/\s+/g, '-')}.png`, { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `I'm dining at ${restaurantName}`,
          text: `Check out ${restaurantName} on DiningTracker.co.za`,
          files: [file],
        });
      } else {
        // Fallback: download
        downloadCard(canvas);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const canvas = await generateCard();
      downloadCard(canvas);
      toast.success('Card downloaded!');
    } catch {
      toast.error('Failed to generate card');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCard = (canvas) => {
    const link = document.createElement('a');
    link.download = `checkin-${restaurantName?.replace(/\s+/g, '-') || 'restaurant'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="flex gap-2" data-testid="share-checkin-card">
      <Button
        onClick={handleShare}
        disabled={generating}
        className="flex-1 bg-[#1A1918] hover:bg-[#2C2826] text-white rounded-xl"
        data-testid="share-checkin-btn"
      >
        {generating ? (
          <SpinnerGap className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <ShareNetwork weight="bold" className="w-5 h-5 mr-2" />
            Share
          </>
        )}
      </Button>
      <Button
        onClick={handleDownload}
        disabled={generating}
        variant="outline"
        className="rounded-xl border-[#EAE8E3]"
        data-testid="download-checkin-btn"
      >
        <Download weight="bold" className="w-5 h-5" />
      </Button>
    </div>
  );
};

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, 2); // max 2 lines
}

export default ShareCheckinCard;
